import { useEffect } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useCreateProduct, useDeleteProduct } from 'services/calculations/globalProducts/hooks';

import { concatWithDot } from 'helpers/string';
import ToastService from 'helpers/ToastService';

import { initialPeriodsTableRow } from '../constants/periods';
import {
  PRODUCTION_NAMES,
  prefix as productionPrefix,
} from '../Sections/BusinessOperations/ProductionSection/utils/form';
import { COST_OF_EACH_UNIT_FORM_NAMES } from '../Sections/FinancialPlan/PlannedExpenses/CostOfEachUnitForm/utils/form';
import { costOfEachUnitPrefix } from '../Sections/FinancialPlan/PlannedExpenses/utils/form';
import { REVENUE_PLAN_NAMES, prefix as revenuePlanPrefix } from '../Sections/FinancialPlan/RevenuePlan/utils/form';
import { GLOBAL_PRODUCTS_NAME } from '../Sections/Global/form';
import { IUseFieldArrayReturnValue, PRODUCT_NAMES, productInitialValues } from '../Sections/Global/general/products';
import { SALES_NAMES, prefix as salesPlanPrefix } from '../Sections/MarketingAndSales/SalesPlanSection/utils/form';
import { handleChangeTableOnProductDelete } from '../utils/helpers';

export const useGlobalProducts = () => {
  const { control, setValue } = useFormContext();
  const { id: businessPlanId = '' } = useParams();

  const createProduct = useCreateProduct(businessPlanId);
  const deleteProduct = useDeleteProduct(businessPlanId);

  // ---- PRODUCTS ----
  const {
    fields: products,
    append: appendProduct,
    remove: removeProduct,
    insert: insertProduct,
  } = useFieldArray({
    control,
    name: GLOBAL_PRODUCTS_NAME,
  }) as IUseFieldArrayReturnValue;

  // Watch for product names specifically
  const watchedProducts = useWatch({
    control,
    name: GLOBAL_PRODUCTS_NAME,
  });

  // Watch for product name changes and update productsName
  useEffect(() => {
    if (!watchedProducts) return;

    const productsName = watchedProducts.reduce(
      (acc: Record<string, string>, product: Record<string, any>, index: number) => {
        const name = product[PRODUCT_NAMES.NAME];
        acc[`column${index}`] = typeof name === 'string' ? name : '';
        return acc;
      },
      {} as Record<string, string>,
    );

    setValue(
      concatWithDot(costOfEachUnitPrefix, COST_OF_EACH_UNIT_FORM_NAMES.COST_OF_EACH_UNIT_PRODUCTS_NAMES),
      productsName,
    );
  }, [watchedProducts, setValue]);

  // ---- MARKETING AND SALES -> SALES_PLAN ----

  const {
    fields: salesPlanFields,
    append: appendSalesPlanRow,
    remove: removeSalesPlanRow,
    insert: insertSalesPlanRow,
  } = useFieldArray({ control, name: concatWithDot(salesPlanPrefix, SALES_NAMES.PLANNED_PRODUCTS_VOLUME) });

  // ---- BUSINESS OPERATIONS -> PRODUCTION ----

  const {
    fields: productionFields,
    append: appendProductionRow,
    remove: removeProductionRow,
    insert: insertProductionRow,
  } = useFieldArray({ control, name: concatWithDot(productionPrefix, PRODUCTION_NAMES.PLANNED_PRODUCTS_VOLUME) });

  // ---- FINANCIAL PLAN -> REVENUE PLAN ----

  const {
    fields: revenuePlanFields,
    append: appendRevenuePlanRow,
    remove: removeRevenuePlanRow,
    insert: insertRevenuePlanRow,
  } = useFieldArray({ control, name: concatWithDot(revenuePlanPrefix, REVENUE_PLAN_NAMES.PLANNED_PRODUCTS_VOLUME) });

  const detailCostPerProduct = useWatch({
    name: concatWithDot(costOfEachUnitPrefix, COST_OF_EACH_UNIT_FORM_NAMES.COST_OF_EACH_UNIT_TABLE),
  });

  // ---- HANDLERS ----

  const handleAddProduct = async (values?: any) => {
    try {
      if (!businessPlanId) {
        return;
      }

      const { id: productId } = await createProduct.mutateAsync(businessPlanId);

      const productWithId = {
        ...productInitialValues,
        productId,
        ...(typeof values === 'object' && values !== null && !values.nativeEvent && values),
      };

      appendProduct(productWithId);
      appendSalesPlanRow(initialPeriodsTableRow, { shouldFocus: false });
      appendProductionRow(initialPeriodsTableRow);
      appendRevenuePlanRow(initialPeriodsTableRow, { shouldFocus: false });
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }
    }
  };

  const handleRemoveProduct = async (index: number) => {
    const product = products[index];
    const salesPlanField = salesPlanFields[index];
    const productionField = productionFields[index];
    const revenuePlanField = revenuePlanFields[index];
    try {
      const id = product.productId;
      if (!id) {
        return ToastService.showError('Id is required');
      }

      removeProduct(index);
      removeSalesPlanRow(index);
      removeProductionRow(index);
      removeRevenuePlanRow(index);

      await deleteProduct.mutateAsync({ id });

      const changedTable = handleChangeTableOnProductDelete(detailCostPerProduct, index, products.length);
      setValue(concatWithDot(costOfEachUnitPrefix, COST_OF_EACH_UNIT_FORM_NAMES.COST_OF_EACH_UNIT_TABLE), changedTable);
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }

      insertProduct(index, product);
      insertSalesPlanRow(index, salesPlanField);
      insertProductionRow(index, productionField);
      insertRevenuePlanRow(index, revenuePlanField);
    }
  };

  return {
    products,
    salesPlanFields,
    productionFields,
    revenuePlanFields,
    handleAddProduct,
    handleRemoveProduct,
  };
};
